"use client"

import React from "react"
import { useUbPDPState } from "@/store/ubPDPState"

import { CardPrices, determineCardPrices } from "@/lib/price-utils"
import { PdpContext } from "@/lib/storefront/product/pdp-service"
import { FullMasterProductData, ReducedProductData } from "@/lib/storefront/product/product"
import { showProductPrice } from "@/lib/storefront/product/productHelper"
import { cn } from "@/components/ui/helpers/utils"
import { usePdpDynamicContext } from "@/components/product/pdp/usePdpDynamicContext"
import { AddToBasketWithQuantityInputOptionalClient } from "@/components/product/pdp/buybox/AddToBasketWithQuantityInputOptionalClient"
import { ProductDetailBuyboxAddToWishlistActionClient } from "@/components/product/pdp/buybox/ProductDetailBuyboxAddToWishlistActionClient"
import { UseFilteredVariantsHook } from "@/components/product/pdp/UseFilteredVariantsHook"
import { UbProductAddToWishlistActionStoryblok } from "@/components/storyblok/component-types"

export function ProductDetailBuyboxAddToBasketButtonClient(props: {
  pdpContext: PdpContext
  priceRequestLink: string
  addToWishlistBlok?: UbProductAddToWishlistActionStoryblok
}) {
  const pdpContext = usePdpDynamicContext(props.pdpContext)
  const article: ReducedProductData | undefined = pdpContext.productAndArticle.article
  const masterProduct: FullMasterProductData | undefined = pdpContext.productAndArticle.masterProduct

  const { useClientSelectedFilterOptions } = useUbPDPState()

  const cardPrices: CardPrices = determineCardPrices(
    pdpContext.isoLocale,
    pdpContext.isB2B,
    pdpContext.prices,
    article?.ecoTax
  )
  const { showPrice, needsPriceQuotation: needsPriceQuotation } = showProductPrice(article, cardPrices)
  const needsDeliveryQuotation = article?.availability === false
  const priceRequestHref =
    (needsPriceQuotation || needsDeliveryQuotation) && article?.id ? props.priceRequestLink + article.id.toString() : ""

  const filteredVariants = UseFilteredVariantsHook(masterProduct).filteredVariants
  if (filteredVariants.length !== 1 && !article) {
    return null
  }

  return (
    <div
      className={cn("flex w-full flex-row items-center justify-start gap-4 py-2", {
        hidden: !useClientSelectedFilterOptions,
      })}
    >
      <AddToBasketWithQuantityInputOptionalClient
        isoLocale={pdpContext.isoLocale}
        isB2BCustomer={pdpContext.isB2B}
        localState={false}
        productSku={article?.id!}
        article={article}
        prices={pdpContext.prices?.data?.[0]}
        minOrderQuantity={article?.minOrderQuantity}
        orderQuantitySteps={article?.stepOrderQuantity}
        showPrice={showPrice}
        needsPriceQuotation={needsPriceQuotation}
        needsDeliveryQuotation={needsDeliveryQuotation}
        forceRequestPriceLabel={true}
        priceRequestHref={priceRequestHref}
        simulateProductClick={false}
      />

      <ProductDetailBuyboxAddToWishlistActionClient
        blok={props.addToWishlistBlok}
        wishlists={pdpContext.wishlists}
        isAnonymous={pdpContext.isAnonymous}
        sbLocale={pdpContext.sbLocale}
        article={article}
      />
    </div>
  )
}
